import React from 'react';

import PropTypes from 'prop-types';

import PageBack from './PageBack';
import PageForward from './PageForward';
import PageNumber from './PageNumber';

/**
 * Helper function to render pagination pages based on batch logic
 * @param {Object} params - Parameters for rendering
 * @param {number} params.currentBatch - Current batch index
 * @param {Array} params.batches - Array of page number batches
 * @param {number} params.currentPage - Current page number
 * @param {string} params.pathname - Current URL path
 * @param {Object} params.searchParams - URL search parameters
 * @param {Function} params.onPageClick - Page click handler
 * @param {Function} params.onPageForwardClick - Forward click handler
 * @param {Function} params.onPageBackClick - Back click handler
 * @param {Function} params.onPageForwardEnter - Forward enter handler
 * @param {Function} params.onPageBackEnter - Back enter handler
 * @returns {React.ReactElement} - Rendered pagination elements
 */
const renderBatchSwitcher = ({
    currentBatch,
    batches,
    currentPage,
    pathname,
    searchParams,
    onPageClick,
    onPageForwardClick,
    onPageBackClick,
    onPageForwardEnter,
    onPageBackEnter,
}) => {
    const firstBatch = 0;
    const lastBatch = batches.length - 1;

    if (currentBatch === firstBatch) {
        return (
            <>
                {
                    batches[firstBatch].map((pageNumber) => (
                        <PageNumber
                            key={`page-number-${pageNumber}`}
                            isCurrentPage={currentPage === pageNumber}
                            number={pageNumber}
                            pathname={pathname}
                            searchParams={searchParams}
                            onClick={onPageClick}
                        />
                    ))
                }
                <PageForward onClick={onPageForwardClick} onKeyDown={onPageForwardEnter} />
            </>
        );
    }

    if (currentBatch === lastBatch) {
        return (
            <>
                <PageBack onClick={onPageBackClick} onKeyDown={onPageBackEnter} />
                <PageNumber
                    key="page-number-1"
                    number={1}
                    searchParams={searchParams}
                    pathname={pathname}
                    onClick={onPageClick}
                />
                ...
                {
                    batches[lastBatch].map((pageNumber) => (
                        <PageNumber
                            key={`page-number-${pageNumber}`}
                            isCurrentPage={currentPage === pageNumber}
                            number={pageNumber}
                            pathname={pathname}
                            searchParams={searchParams}
                            onClick={onPageClick}
                        />
                    ))
                }
            </>
        );
    }

    return (
        <>
            <PageBack onClick={onPageBackClick} onKeyDown={onPageBackEnter} />
            <PageNumber
                key="page-number-1"
                number={1}
                searchParams={searchParams}
                pathname={pathname}
                onClick={onPageClick}
            />
            ...
            {
                batches[currentBatch].map((pageNumber) => (
                    <PageNumber
                        key={`page-number-${pageNumber}`}
                        isCurrentPage={currentPage === pageNumber}
                        number={pageNumber}
                        searchParams={searchParams}
                        pathname={pathname}
                        onClick={onPageClick}
                    />
                ))
            }
            <PageForward onClick={onPageForwardClick} onKeyDown={onPageForwardEnter} />
        </>
    );
};

/**
 * Helper function to render simple pagination (8 or fewer pages)
 * @param {Object} params - Parameters for rendering
 * @param {Array} params.pageNumbers - Array of page numbers
 * @param {number} params.currentPage - Current page number
 * @param {string} params.pathname - Current URL path
 * @param {Object} params.searchParams - URL search parameters
 * @param {Function} params.onPageClick - Page click handler
 * @returns {React.ReactElement} - Rendered pagination elements
 */
const renderSimplePagination = ({
    pageNumbers,
    currentPage,
    pathname,
    searchParams,
    onPageClick,
}) => {
    return pageNumbers.map((pageNumber, i) => {
        if (i === 0) {
            return null;
        }

        return (
            <PageNumber
                key={`page-number-${pageNumber}`}
                isCurrentPage={currentPage === pageNumber}
                number={pageNumber}
                pathname={pathname}
                searchParams={searchParams}
                onClick={onPageClick}
            />
        );
    });
};

/**
 * @param {number} currentBatch - Current batch index (0-based)
 * @param {Array} batches - Array of page number batches
 * @param {Array} pageNumbers - Array of all page numbers
 * @param {number} currentPage - Current page number
 * @param {string} pathname - Current URL pathname
 * @param {Object} searchParams - URL search parameters object
 * @param {Function} onPageClick - Handler for individual page clicks
 * @param {Function} onPageForwardClick - Handler for forward navigation
 * @param {Function} onPageBackClick - Handler for backward navigation
 * @param {Function} onPageForwardEnter - Handler for forward Enter key
 * @param {Function} onPageBackEnter - Handler for backward Enter key
 * @returns {React.ReactElement} - Pagination navigation component
 *
 * @example
 * // With batching (more than 8 pages)
 * <PageSwitcher
 *   currentBatch={0}
 *   batches={[[1,2,3,4,5,6,7], [8,9,10,11]]}
 *   pageNumbers={[1,2,3,4,5,6,7,8,9,10,11]}
 *   currentPage={1}
 *   pathname="/products"
 *   searchParams={{}}
 *   onPageClick={handlePageClick}
 *   onPageForwardClick={handleForward}
 *   onPageBackClick={handleBack}
 *   onPageForwardEnter={handleForwardEnter}
 *   onPageBackEnter={handleBackEnter}
 * />
 */
const PageSwitcher = ({
    currentBatch,
    batches,
    pageNumbers,
    currentPage,
    pathname,
    searchParams,
    onPageClick,
    onPageForwardClick,
    onPageBackClick,
    onPageForwardEnter,
    onPageBackEnter,
}) => {
    let pages = null;

    // pageNumbers includes 0 as first element, so actual pages = pageNumbers.length - 1
    const actualPageCount = pageNumbers.length - 1;

    if (actualPageCount > 8) {
        pages = renderBatchSwitcher({
            currentBatch,
            batches,
            currentPage,
            pathname,
            searchParams,
            onPageClick,
            onPageForwardClick,
            onPageBackClick,
            onPageForwardEnter,
            onPageBackEnter,
        });
    } else {
        pages = renderSimplePagination({
            pageNumbers,
            currentPage,
            pathname,
            searchParams,
            onPageClick,
        });
    }

    return (
        <nav role="navigation" aria-label="Pagination Navigation">
            {pages}
        </nav>
    );
};

PageSwitcher.propTypes = {
    currentBatch: PropTypes.number.isRequired,
    batches: PropTypes.arrayOf(
        PropTypes.arrayOf(PropTypes.number),
    ).isRequired,
    pageNumbers: PropTypes.arrayOf(PropTypes.number).isRequired,
    currentPage: PropTypes.number,
    pathname: PropTypes.string.isRequired,
    searchParams: PropTypes.object.isRequired,
    onPageClick: PropTypes.func.isRequired,
    onPageForwardClick: PropTypes.func.isRequired,
    onPageBackClick: PropTypes.func.isRequired,
    onPageForwardEnter: PropTypes.func.isRequired,
    onPageBackEnter: PropTypes.func.isRequired,
};

export default React.memo(PageSwitcher);

